import { Eyebrow, Reveal } from "@/components/Reveal";

const DELIVERABLES = [
  ["Week 1", "Your Brand Foundation"],
  ["Week 2", "Your Visibility Practice"],
  ["Week 3", "Your Brand Blueprint"],
  ["Week 4", "Your Visibility Challenge"],
  ["Week 5", "Your 30-Day Content System"],
  ["Week 6", "Your First Offer"],
  ["Week 7", "Your Sales System"],
  ["Week 8", "Your Launch Plan"],
];

const OUTCOMES = [
  {
    t: "A brand people instantly understand",
    d: "A clear message you can explain in one sentence, plus positioning around what you do and who you're for.",
  },
  {
    t: "A profile that turns attention into interest",
    d: "Your bio, positioning, and profile clearly communicate who you are, what you do, and why people should care.",
  },
  {
    t: "90 days of scroll-stopping content",
    d: "Your content pillars, ideas, hooks, and posting plan mapped out so you're never staring at a blank screen.",
  },
  {
    t: "Your ideas out of your head and into the world",
    d: "You won't just plan content — you'll actually publish it during the 8 weeks.",
  },
  {
    t: "An offer people can actually buy",
    d: "A clear problem you solve, a named offer, defined deliverables, and a price you feel confident standing behind.",
  },
  {
    t: "A simple way to sell without feeling salesy",
    d: "A clear process for starting conversations, communicating your offer, and moving people toward a decision.",
  },
  {
    t: "Real practice asking for the sale",
    d: "We'll practice sales conversations, objections, and follow-ups until selling feels less intimidating and more natural.",
  },
  {
    t: "A 30-day plan to launch and keep moving",
    d: "You'll leave knowing exactly what to post, promote, and do next — instead of losing momentum after the program ends.",
  },
  {
    t: "Tools to stop letting fear run your brand",
    d: "Learn how to work through perfectionism, comparison, judgment, and the resistance that keeps you from being seen.",
  },
  {
    t: "A system you can keep building on",
    d: "Not another course full of information you'll forget. A practical foundation you can continue using long after the 8 weeks are over.",
  },
];

export const Timeline = () => (
  <section
    id="weeks"
    className="bg-ink py-24 lg:py-32"
    data-testid="timeline-section"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div>
          <Eyebrow>The 8-week deliverables</Eyebrow>
          <h2 className="mt-6 font-mont text-3xl font-bold leading-[1.15] tracking-tight text-bone sm:text-4xl lg:text-5xl">
            What you&rsquo;ll <em className="italic text-forest">actually</em>{" "}
            build.
          </h2>
        </div>
        <p className="max-w-sm font-mont text-sm italic leading-relaxed text-white/50">
          Not ideas in a doc — tangible builds, week by week, with the
          receipts to show for it.
        </p>
      </div>

      <Reveal delay={0.05}>
        <div className="relative mt-14" data-testid="deliverables-grid">
          <div
            className="absolute left-[7px] top-3 h-[calc(100%-36px)] w-px bg-white/10"
            aria-hidden="true"
          />
          {DELIVERABLES.map(([week, item], i) => (
            <div
              key={week}
              className="relative flex items-center gap-6 py-4 pl-10"
              data-testid={`deliverable-${i + 1}`}
            >
              <span
                className="absolute left-0 top-1/2 h-[15px] w-[15px] -translate-y-1/2 rounded-full border-2 border-forest bg-ink"
                aria-hidden="true"
              />
              <span className="w-16 shrink-0 font-mont text-[11px] font-bold uppercase tracking-[0.18em] text-stone">
                {week}
              </span>
              <span className="font-mont text-lg font-bold text-forest sm:text-xl">
                {item}
              </span>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <p className="mt-16 font-mont text-2xl font-bold text-bone sm:text-3xl">
          What you&rsquo;ll leave with
        </p>
        <ol className="mt-8 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
          {OUTCOMES.map((o, i) => (
            <li
              key={o.t}
              className={`flex items-start gap-4 border-b border-white/10 px-4 py-5 ${
                i % 2 === 0 ? "bg-white/[0.06]" : "bg-black"
              }`}
              data-testid={`outcome-${i + 1}`}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-forest font-mont text-[11px] font-bold tracking-wide text-white">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <p className="font-mont text-sm font-bold leading-snug text-bone">
                  {o.t}
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-white/60">
                  {o.d}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </Reveal>
    </div>
  </section>
);

export default Timeline;
